import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/layout/Layout';
import Pagination from '../components/common/Pagination';
import ReportQuestionModal from '../components/common/ReportQuestionModal';
import UpgradeModal from '../components/common/UpgradeModal';
import ModuleAccessOverlay from '../components/common/ModuleAccessOverlay';
import { VideoModal, WatchVideoButton } from '../components/common/VideoModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../components/ui/command';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../components/ui/hover-card';
import { 
  ChevronLeft, ChevronDown, ChevronRight, Bookmark, BookmarkCheck,
  Loader2, FileText, RefreshCw, Calendar, Filter, Info, Circle, CheckCircle2, RotateCcw, Check, ChevronsUpDown, Flag, ZoomIn, X, Lock, Sparkles, FolderTree, Search
} from 'lucide-react';
import { cn } from '../lib/utils';
import TopicPicker from '../components/shared/TopicPicker';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePreferences } from '../context/PreferencesContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { useModuleAccess } from '../hooks/useModuleAccess';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WrittenAllQuestionsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { preferences } = usePreferences();
  const { isPaid, loading: subscriptionAccessLoading } = useSubscriptionAccess();
  
  // Module access check
  const { getWrittenStatus, loading: moduleLoading } = useModuleAccess();
  const userGrade = preferences?.grade;
  const writtenStatus = userGrade ? getWrittenStatus(userGrade) : null;
  
  const functionId = searchParams.get('function');
  
  const [questions, setQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [functionInfo, setFunctionInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookmarkedIds, setBookmarkedIds] = useState(new Set());
  const [completedIds, setCompletedIds] = useState(new Set());
  
  // Dropdown Filters
  const [selectedTopic, setSelectedTopic] = useState('all');
  const [topicSearchOpen, setTopicSearchOpen] = useState(false);
  const [askedCountFilter, setAskedCountFilter] = useState('all');
  const [fromYear, setFromYear] = useState('all');
  const [fromMonth, setFromMonth] = useState('all');
  const [toYear, setToYear] = useState('all');
  const [toMonth, setToMonth] = useState('all');
  
  // Search filter
  const [searchQuery, setSearchQuery] = useState('');
  
  // Button Filters - 'all', 'completed', 'incomplete', 'bookmarked'
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Expanded answers
  const [expandedAnswers, setExpandedAnswers] = useState(new Set());
  
  // Subscription state
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  
  // Image lightbox state
  const [enlargedImage, setEnlargedImage] = useState(null);
  
  // Report modal state
  const [reportModal, setReportModal] = useState({
    open: false,
    questionId: null,
    questionText: ''
  });

  // Explainer video modal state
  const [videoModal, setVideoModal] = useState({ open: false, url: null });
  
  // Helper function to get proper image URL
  const getImageUrl = (img) => {
    if (!img || !img.url) return '';
    if (img.url.startsWith('http')) return img.url;
    return `${BACKEND_URL}${img.url}`;
  };
  
  // Available months for dropdowns
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'];
  
  // Available years (current year and 10 years back for better range)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);

  // Get available months for selected "from" year
  const availableFromMonths = useMemo(() => {
    if (fromYear === 'all') return [];
    return months;
  }, [fromYear]);

  // Get available months for selected "to" year
  const availableToMonths = useMemo(() => {
    if (toYear === 'all') return [];
    return months;
  }, [toYear]);

  // Organize topics into parent-child hierarchy for display
  const getHierarchicalTopics = useMemo(() => {
    // Separate parents (no parent_topic_id) and children
    const parentTopics = topics.filter(t => !t.parent_topic_id);
    const childTopics = topics.filter(t => t.parent_topic_id);
    
    // Build hierarchy
    const hierarchy = [];
    parentTopics.forEach(parent => {
      hierarchy.push({ ...parent, isParent: true, level: 0 });
      const children = childTopics.filter(c => c.parent_topic_id === parent.id);
      children.forEach(child => {
        hierarchy.push({ ...child, isParent: false, level: 1 });
      });
    });
    
    // Add orphan children (topics with parent_topic_id but parent not in current filter)
    const addedChildIds = new Set(hierarchy.filter(t => t.level === 1).map(t => t.id));
    childTopics.forEach(child => {
      if (!addedChildIds.has(child.id)) {
        hierarchy.push({ ...child, isParent: false, level: 0 }); // Show as top-level
      }
    });
    
    return hierarchy;
  }, [topics]);

  useEffect(() => {
    if (!functionId) {
      navigate('/written-bank');
      return;
    }
    
    checkSubscription();
    
    if (userGrade) {
      fetchData();
      if (user) {
        fetchBookmarks();
        fetchCompleted();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [functionId, userGrade, user]);

  const checkSubscription = async () => {
    // Only check subscription if authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      // Guest users default to free status (answers locked)
      setSubscriptionStatus({ subscription_status: 'free', is_active: false });
      return;
    }
    
    try {
      const response = await axios.get(`${API}/user/subscription`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to check subscription:', error);
      // If API fails, default to free
      setSubscriptionStatus({ subscription_status: 'free', is_active: false });
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const params = {
        grade: userGrade,
        function_id: functionId
      };
      
      const response = await axios.get(`${API}/written/questions/public`, { params });
      setQuestions(response.data.questions || []);
      setTopics(response.data.topics || []);
      
      // Get function info
      const filtersRes = await axios.get(`${API}/written/filters/public`);
      const func = (filtersRes.data.functions || []).find(f => f.id === functionId);
      setFunctionInfo(func);
      
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookmarks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/written/bookmarks`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setBookmarkedIds(new Set(response.data.bookmarked_ids || []));
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
    }
  };

  const fetchCompleted = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/written/completed`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCompletedIds(new Set(response.data.completed_ids || []));
    } catch (error) {
      console.error('Failed to fetch completed:', error);
    }
  };

  const toggleBookmark = async (questionId) => {
    if (!user) {
      toast.error('Please login to bookmark questions');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/written/bookmarks/${questionId}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setBookmarkedIds(prev => {
        const newSet = new Set(prev);
        if (response.data.bookmarked) {
          newSet.add(questionId);
          toast.success('Question bookmarked');
        } else {
          newSet.delete(questionId);
          toast.success('Bookmark removed');
        }
        return newSet;
      });
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
      toast.error('Failed to update bookmark');
    }
  };

  const toggleCompleted = async (questionId) => {
    if (!user) {
      toast.error('Please login to track progress');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/written/completed/${questionId}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCompletedIds(prev => {
        const newSet = new Set(prev);
        if (response.data.completed) {
          newSet.add(questionId);
        } else {
          newSet.delete(questionId);
        }
        return newSet;
      });
    } catch (error) {
      console.error('Failed to toggle completed:', error);
      toast.error('Failed to update progress');
    }
  };

  const toggleAnswer = async (questionId) => {
    // Check if user has paid access for the current grade
    const subscribedGrade = subscriptionStatus?.subscribed_grade;
    const userRole = subscriptionStatus?.user_role;
    const currentGrade = userGrade;
    
    // Admins/sub-admins have access to all grades
    const isAdmin = userRole === 'admin' || userRole === 'sub_admin';
    
    // Check if user has access: either admin, or subscribed to 'all', or subscribed to current grade
    const hasGradeAccess = isAdmin || subscribedGrade === 'all' || subscribedGrade === currentGrade;
    const hasAccess = isAdmin || (subscriptionStatus?.subscription_status === 'paid' && subscriptionStatus?.is_active && hasGradeAccess);
    
    if (!hasAccess) {
      // User doesn't have access - show upgrade modal
      setShowUpgrade(true);
      return;
    }
    
    // User has access - toggle answer
    const isExpanding = !expandedAnswers.has(questionId);
    
    setExpandedAnswers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
    
    // Track as "viewed" when expanding answer (for progress tracker)
    if (isExpanding && user) {
      try {
        await axios.post(`${API}/progress/action`, {
          question_id: questionId,
          action: 'view',
          question_type: 'written'
        });
      } catch (error) {
        // Silent fail - don't interrupt user experience
        console.error('Failed to track view:', error);
      }
    }
  };

  // Get topic name by ID
  const getTopicName = (topicId) => {
    const topic = topics.find(t => t.id === topicId);
    return topic ? topic.name : null;
  };

  // Check if a date is in range
  const isInDateRange = (appearances) => {
    // No filter active - show all
    if (fromYear === 'all' && toYear === 'all') return true;
    
    // No appearances - exclude if date filter is active
    if (!appearances || appearances.length === 0) return false;
    
    const fromDate = fromYear !== 'all' ? 
      new Date(parseInt(fromYear), fromMonth !== 'all' ? months.indexOf(fromMonth) : 0) : null;
    const toDate = toYear !== 'all' ? 
      new Date(parseInt(toYear), toMonth !== 'all' ? months.indexOf(toMonth) : 11, 31) : null;
    
    return appearances.some(app => {
      const examMonth = app.exam_month || '';
      // Match format: "January 2026" or "Jan 2026"
      const match = examMonth.match(/(\w+)\s+(\d{4})/);
      if (!match) return false; // No valid date - exclude
      
      const monthName = match[1];
      const year = parseInt(match[2]);
      
      // Try full month name first, then abbreviated
      let monthIndex = months.indexOf(monthName);
      if (monthIndex === -1) {
        // Try abbreviated month names
        const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        monthIndex = shortMonths.indexOf(monthName);
      }
      if (monthIndex === -1) return false; // Invalid month - exclude
      
      const appDate = new Date(year, monthIndex);
      
      if (fromDate && toDate) {
        return appDate >= fromDate && appDate <= toDate;
      } else if (fromDate) {
        return appDate >= fromDate;
      } else if (toDate) {
        return appDate <= toDate;
      }
      return true;
    });
  };

  // Get currently filtered questions that are completed (for reset)
  const getCompletedFilteredQuestionIds = () => {
    // Apply all dropdown filters (topic, asked count, date range) but not status filter
    const filtered = questions.filter(q => {
      if (selectedTopic !== 'all' && q.topic_id !== selectedTopic) return false;
      if (askedCountFilter === '2plus' && (q.asked_count || 1) < 2) return false;
      if (askedCountFilter === '3plus' && (q.asked_count || 1) < 3) return false;
      if (!isInDateRange(q.appearances)) return false;
      return true;
    });
    
    // Return only the completed ones
    return filtered.filter(q => completedIds.has(q.id)).map(q => q.id);
  };

  // Check if any filters are active (excluding status filter)
  const hasActiveFilters = selectedTopic !== 'all' || askedCountFilter !== 'all' || 
    fromYear !== 'all' || fromMonth !== 'all' || toYear !== 'all' || toMonth !== 'all';

  const handleResetCompleted = async () => {
    if (!user) return;
    
    const completedToReset = getCompletedFilteredQuestionIds();
    
    if (completedToReset.length === 0) {
      toast.info('No completed questions to reset');
      return;
    }
    
    const filterMessage = hasActiveFilters 
      ? `This will reset ${completedToReset.length} completed questions matching your current filters.`
      : `This will reset all ${completedToReset.length} completed questions.`;
    
    if (!window.confirm(`Are you sure? ${filterMessage}`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/written/completed/reset`, 
        { question_ids: completedToReset },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      
      toast.success(response.data.message || 'Progress reset successfully');
      
      // Reset local completed state for the reset questions
      setCompletedIds(prev => {
        const newSet = new Set(prev);
        completedToReset.forEach(id => newSet.delete(id));
        return newSet;
      });
    } catch (error) {
      console.error('Failed to reset progress:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset progress');
    }
  };

  // Calculate counts for status filter buttons
  const statusCounts = useMemo(() => {
    const completedCount = questions.filter(q => completedIds.has(q.id)).length;
    const incompleteCount = questions.length - completedCount;
    const bookmarkedCount = questions.filter(q => bookmarkedIds.has(q.id)).length;
    return { completedCount, incompleteCount, bookmarkedCount };
  }, [questions, completedIds, bookmarkedIds]);

  // Filter questions - apply dropdown filters first, then status filter
  const filteredQuestions = useMemo(() => {
    let result = questions.filter(q => {
      // Search filter - check question text and answer text
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        const questionMatch = q.question_text?.toLowerCase().includes(query);
        const answerMatch = q.answer_text?.toLowerCase().includes(query);
        if (!questionMatch && !answerMatch) return false;
      }
      
      // Topic filter - also include subtopics if parent topic selected
      if (selectedTopic !== 'all') {
        const selectedTopicData = topics.find(t => t.id === selectedTopic);
        if (selectedTopicData && !selectedTopicData.parent_topic_id) {
          // Parent topic selected - include this topic and all its children
          const childTopicIds = topics
            .filter(t => t.parent_topic_id === selectedTopic)
            .map(t => t.id);
          const validTopicIds = [selectedTopic, ...childTopicIds];
          if (!validTopicIds.includes(q.topic_id)) return false;
        } else {
          // Subtopic selected - exact match only
          if (q.topic_id !== selectedTopic) return false;
        }
      }
      
      // Asked count filter
      if (askedCountFilter === '2plus' && (q.asked_count || 1) < 2) return false;
      if (askedCountFilter === '3plus' && (q.asked_count || 1) < 3) return false;
      
      // Date range filter
      if (!isInDateRange(q.appearances)) return false;
      
      return true;
    });
    
    // Apply status filter
    if (statusFilter === 'completed') {
      result = result.filter(q => completedIds.has(q.id));
    } else if (statusFilter === 'incomplete') {
      result = result.filter(q => !completedIds.has(q.id));
    } else if (statusFilter === 'bookmarked') {
      result = result.filter(q => bookmarkedIds.has(q.id));
    }
    
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, topics, selectedTopic, searchQuery, askedCountFilter, fromYear, fromMonth, toYear, toMonth, statusFilter, completedIds, bookmarkedIds]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTopic, searchQuery, askedCountFilter, fromYear, fromMonth, toYear, toMonth, statusFilter]);

  // Pagination logic
  const totalItems = filteredQuestions.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedQuestions = filteredQuestions.slice(startIndex, endIndex);

  // Toggle status filter - click same filter again to reset to 'all'
  const handleStatusFilterClick = (filter) => {
    setStatusFilter(prev => prev === filter ? 'all' : filter);
  };

  // Calculate completion progress for filtered questions
  const completionCount = useMemo(() => {
    return filteredQuestions.filter(q => completedIds.has(q.id)).length;
  }, [filteredQuestions, completedIds]);

  if (!userGrade) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-gray-500">Please set your grade in your profile</p>
          <Button onClick={() => navigate('/profile')} className="mt-4">
            Go to Profile
          </Button>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#14B8A6]" />
        </div>
      </Layout>
    );
  }

  // Module access enforcement - show overlay if written is not active
  if (!moduleLoading) {
    // No grade selected - prompt user to select one
    if (!userGrade) {
      return (
        <Layout>
          <ModuleAccessOverlay
            status="disabled"
            featureName="Written Question Bank"
            customMessage="Please select your grade and MMD location first to access the Written Question Bank."
            showBackButton={true}
            showHomeButton={false}
          />
          <div className="flex justify-center mt-4">
            <Button
              onClick={() => navigate('/grade')}
              className="bg-[#EC4899] hover:bg-[#EC4899]/90"
            >
              Select Grade & MMD
            </Button>
          </div>
        </Layout>
      );
    }
    
    // Grade selected but module is not active for this grade
    if (writtenStatus && writtenStatus !== 'active') {
      return (
        <Layout>
          <ModuleAccessOverlay
            status={writtenStatus}
            featureName="Written Question Bank"
            description={
              writtenStatus === 'coming_soon'
                ? `The Written Question Bank for ${userGrade} is coming soon! We're preparing this content and it will be available shortly.`
                : writtenStatus === 'hidden'
                  ? `The Written Question Bank is not available for ${userGrade}. Please select a different grade or contact support.`
                  : `The Written Question Bank is currently not available for ${userGrade}.`
            }
          />
        </Layout>
      );
    }
  }

  return (
    <Layout>
      <div className="page-transition">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/written-bank')}
            className="mb-2"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Back to Papers
          </Button>
          
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="static" className="bg-[#14B8A6] text-white">{userGrade}</Badge>
                {functionInfo && (
                  <Badge variant="outline" className="max-w-full whitespace-normal break-words text-left">{functionInfo.display}</Badge>
                )}
              </div>
              <h1 className="text-2xl font-bold text-gray-900 break-words">All Questions</h1>
              <p className="text-gray-600 break-words">{functionInfo?.name || 'All functions'}</p>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge variant="static" className="bg-gray-100 text-gray-700 text-sm px-3 py-1">
                {filteredQuestions.length} of {questions.length} questions
              </Badge>
              {user && (
                <Badge variant="static" className="bg-green-100 text-green-700 text-sm px-3 py-1">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {completionCount}/{filteredQuestions.length} completed
                </Badge>
              )}
            </div>
          </div>
        </div>

        {/* Filters Card */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="font-medium text-gray-700">Filters</span>
            </div>
            
            {/* Search and Dropdown Filters Row */}
            <div className="mb-4">
              {/* Search Input */}
              <div className="mb-4">
                <Label className="text-sm text-gray-600 mb-1 block">Search Questions</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Search in questions and answers..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                  {searchQuery && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                      onClick={() => setSearchQuery('')}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Dropdown Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Searchable Topic Filter with Hierarchy */}
              {topics.length > 0 && (
                <div>
                  <Label className="text-sm text-gray-600 mb-1 block">Topic</Label>
                  <TopicPicker
                    topics={topics}
                    value={selectedTopic === 'all' ? null : selectedTopic}
                    onChange={(id) => setSelectedTopic(id || 'all')}
                    placeholder="All Topics"
                    allowClear
                    testIdPrefix="user-written-topic-filter"
                  />
                  {selectedTopic !== 'all' && topics.find(t => t.id === selectedTopic) && (
                    <p className="text-xs text-amber-600 mt-1">
                      ↳ Showing questions from this topic and all sub-topics
                    </p>
                  )}
                </div>
              )}
              
              {/* Asked Count Filter */}
              <div>
                <Label className="text-sm text-gray-600 mb-1 block">Asked Count</Label>
                <Select value={askedCountFilter} onValueChange={setAskedCountFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Questions</SelectItem>
                    <SelectItem value="2plus">Asked 2+ times</SelectItem>
                    <SelectItem value="3plus">Asked 3+ times</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Date Range Row - Year then Month (cascading) */}
            <div className="mb-4 pt-4 border-t">
              <Label className="text-sm text-gray-600 mb-2 block">Date Range (by appearance)</Label>
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-gray-500 font-medium">From:</span>
                <Select 
                  value={fromYear} 
                  onValueChange={(val) => {
                    setFromYear(val);
                    if (val === 'all') setFromMonth('all');
                  }}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Year</SelectItem>
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={fromMonth} 
                  onValueChange={setFromMonth}
                  disabled={fromYear === 'all'}
                >
                  <SelectTrigger className={`w-[140px] ${fromYear === 'all' ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder={fromYear === 'all' ? 'Select year' : 'Month'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Month</SelectItem>
                    {availableFromMonths.map(month => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <span className="text-sm text-gray-500 font-medium mx-1">To:</span>
                
                <Select 
                  value={toYear} 
                  onValueChange={(val) => {
                    setToYear(val);
                    if (val === 'all') setToMonth('all');
                  }}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue placeholder="Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Year</SelectItem>
                    {years.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select 
                  value={toMonth} 
                  onValueChange={setToMonth}
                  disabled={toYear === 'all'}
                >
                  <SelectTrigger className={`w-[140px] ${toYear === 'all' ? 'opacity-50' : ''}`}>
                    <SelectValue placeholder={toYear === 'all' ? 'Select year' : 'Month'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Month</SelectItem>
                    {availableToMonths.map(month => (
                      <SelectItem key={month} value={month}>{month}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Status Filter Buttons Row */}
            {user && (
              <div className="pt-4 border-t">
                <Label className="text-sm text-gray-600 mb-2 block">Status</Label>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant={statusFilter === 'completed' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleStatusFilterClick('completed')}
                    className={statusFilter === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Completed ({statusCounts.completedCount})
                  </Button>
                  <Button
                    variant={statusFilter === 'incomplete' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleStatusFilterClick('incomplete')}
                    className={statusFilter === 'incomplete' ? 'bg-orange-500 hover:bg-orange-600' : ''}
                  >
                    <Circle className="w-4 h-4 mr-1" />
                    Incomplete ({statusCounts.incompleteCount})
                  </Button>
                  <Button
                    variant={statusFilter === 'bookmarked' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handleStatusFilterClick('bookmarked')}
                    className={statusFilter === 'bookmarked' ? 'bg-[#EC4899] hover:bg-[#EC4899]/90' : ''}
                  >
                    <BookmarkCheck className="w-4 h-4 mr-1" />
                    Bookmarked ({statusCounts.bookmarkedCount})
                  </Button>
                  
                  {/* Reset Button - only show if there are completed questions */}
                  {statusCounts.completedCount > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleResetCompleted}
                      className="text-gray-500 hover:text-red-600 ml-auto"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Reset
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Questions List */}
        <div className="space-y-4">
          {filteredQuestions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No questions match your filters</p>
                {statusFilter !== 'all' && (
                  <Button 
                    variant="link" 
                    onClick={() => setStatusFilter('all')}
                    className="mt-2 text-[#EC4899]"
                  >
                    Show all questions
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            paginatedQuestions.map((question, index) => {
              const isCompleted = completedIds.has(question.id);
              // Calculate actual index for display (considering pagination)
              const displayIndex = startIndex + index + 1;
              return (
                <Card key={question.id} className={`overflow-hidden ${isCompleted ? 'bg-gray-50/50' : ''}`}>
                  <CardContent className="p-0">
                    {/* Question Header */}
                    <div className={`p-4 border-b ${isCompleted ? 'bg-gray-100/50' : 'bg-gray-50'}`}>
                      <div className="flex items-start gap-3">
                        {/* Completion Circle */}
                        <button
                          onClick={() => toggleCompleted(question.id)}
                          className={`mt-1 flex-shrink-0 transition-colors ${
                            user 
                              ? 'cursor-pointer hover:text-green-600' 
                              : 'cursor-not-allowed text-gray-300'
                          }`}
                          title={user ? (isCompleted ? 'Mark as incomplete' : 'Mark as completed') : 'Login to track progress'}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          ) : (
                            <Circle className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        
                        <div className="flex-1">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <span className={`font-bold text-[#EC4899] ${isCompleted ? 'line-through opacity-60' : ''}`}>
                                  Q{displayIndex}.
                                </span>
                                {question.marks && (
                                  <Badge variant="outline" className={`text-xs ${isCompleted ? 'opacity-60' : ''}`}>
                                    {question.marks} marks
                                  </Badge>
                                )}
                                {question.topic_id && (
                                  <Badge variant="static" className={`bg-purple-100 text-purple-800 text-xs max-w-full whitespace-normal break-words text-left ${isCompleted ? 'opacity-60' : ''}`}>
                                    {getTopicName(question.topic_id) || question.topic_name || 'Topic'}
                                  </Badge>
                                )}
                                {/* Asked count with HoverCard for appearances */}
                                {question.asked_count > 1 && question.appearances && question.appearances.length > 1 && (
                                  <HoverCard openDelay={100} closeDelay={100}>
                                    <HoverCardTrigger asChild>
                                      <button 
                                        type="button"
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border cursor-help bg-blue-50 text-blue-700 border-blue-300 ${isCompleted ? 'opacity-60' : ''}`}
                                      >
                                        <RefreshCw className="w-3 h-3" />
                                        Asked {question.asked_count}x
                                        <Info className="w-3 h-3 ml-0.5" />
                                      </button>
                                    </HoverCardTrigger>
                                    <HoverCardContent 
                                      side="top" 
                                      className="w-auto p-3 bg-gray-900 text-white border-gray-700"
                                    >
                                      <p className="font-medium mb-2 text-sm">Asked in:</p>
                                      <ul className="text-xs space-y-1">
                                        {question.appearances.map((app, i) => (
                                          <li key={i} className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3 text-gray-400" />
                                            {app.exam_month}{app.paper_number ? ` (Paper ${app.paper_number})` : ''}
                                          </li>
                                        ))}
                                      </ul>
                                    </HoverCardContent>
                                  </HoverCard>
                                )}
                              </div>
                              <p className={`text-gray-800 whitespace-pre-wrap ${isCompleted ? 'line-through opacity-60' : ''}`}>
                                {question.question_text}
                              </p>
                              
                              {/* Question Images */}
                              {question.question_images?.length > 0 && (
                                <div className="flex gap-3 mt-3 flex-wrap">
                                  {question.question_images.map((img, i) => (
                                    <div 
                                      key={img.id || i}
                                      className="relative group cursor-pointer rounded-lg overflow-hidden border bg-white"
                                      onClick={() => setEnlargedImage({ url: getImageUrl(img), caption: img.caption })}
                                    >
                                      <img 
                                        src={getImageUrl(img)} 
                                        alt={img.caption || `Question image ${i+1}`}
                                        className="max-h-48 w-auto object-contain transition-opacity group-hover:opacity-90"
                                      />
                                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                        <ZoomIn className="w-8 h-8 text-white drop-shadow-lg" />
                                      </div>
                                      {img.caption && (
                                        <p className="text-xs text-gray-600 p-2 bg-gray-50 border-t">
                                          {img.caption}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                            
                            {/* Action Buttons */}
                            <div className="flex items-center gap-1">
                              {/* Flag Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setReportModal({
                                  open: true,
                                  questionId: question.id,
                                  questionText: question.question_text
                                })}
                                className="text-gray-400 hover:text-orange-500"
                                title="Report this question"
                              >
                                <Flag className="w-4 h-4" />
                              </Button>
                              
                              {/* Bookmark Button */}
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => toggleBookmark(question.id)}
                                className={bookmarkedIds.has(question.id) ? 'text-[#EC4899]' : 'text-gray-400'}
                              >
                                {bookmarkedIds.has(question.id) ? (
                                  <BookmarkCheck className="w-5 h-5" />
                                ) : (
                                  <Bookmark className="w-5 h-5" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Answer Section */}
                    {question.has_answer ? (
                      isPaid ? (
                        <Collapsible 
                          open={expandedAnswers.has(question.id)}
                          onOpenChange={() => toggleAnswer(question.id)}
                        >
                          <CollapsibleTrigger className="w-full">
                            <div className="px-4 py-3 flex items-center gap-2 text-sm text-[#EC4899] hover:bg-gray-50 transition-colors">
                              {expandedAnswers.has(question.id) ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                              {expandedAnswers.has(question.id) ? 'Hide Answer' : 'Show Answer'}
                            </div>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="px-4 pb-4">
                              <div className="p-4 bg-green-50 rounded-lg">
                                <p className="text-sm font-medium text-green-800 mb-2 flex items-center gap-2">
                                  Answer:
                                  {question.video_url && (
                                    <WatchVideoButton
                                      videoUrl={question.video_url}
                                      onClick={() => setVideoModal({ open: true, url: question.video_url })}
                                    />
                                  )}
                                </p>
                                <p className="text-green-700 whitespace-pre-wrap">{question.answer_text}</p>
                                
                                {/* Answer Images */}
                                {question.answer_images?.length > 0 && (
                                  <div className="flex gap-3 mt-3 flex-wrap">
                                    {question.answer_images.map((img, i) => (
                                      <div 
                                        key={img.id || i}
                                        className="relative group cursor-pointer rounded-lg overflow-hidden border bg-white"
                                        onClick={() => setEnlargedImage({ url: getImageUrl(img), caption: img.caption })}
                                      >
                                        <img 
                                          src={getImageUrl(img)} 
                                          alt={img.caption || `Answer image ${i+1}`}
                                          className="max-h-48 w-auto object-contain transition-opacity group-hover:opacity-90"
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                                          <ZoomIn className="w-8 h-8 text-white drop-shadow-lg" />
                                        </div>
                                        {img.caption && (
                                          <p className="text-xs text-gray-600 p-2 bg-gray-50 border-t">
                                            {img.caption}
                                          </p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ) : (
                        /* Locked answer for non-paid users */
                        <div 
                          className="px-4 py-3 flex items-center justify-between bg-gradient-to-r from-gray-50 to-blue-50/50 border-t cursor-pointer hover:bg-blue-50/80 transition-colors"
                          onClick={() => setShowUpgrade(true)}
                          data-testid="answer-locked-section"
                        >
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Lock className="w-4 h-4 text-blue-500" />
                            <span>Answer available</span>
                          </div>
                          <Button size="sm" variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50">
                            <Sparkles className="w-3 h-3 mr-1" />
                            Unlock
                          </Button>
                        </div>
                      )
                    ) : (
                      <div className="px-4 py-3 text-sm text-gray-500 italic border-t border-dashed">
                        Answer coming soon
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Pagination */}
        {filteredQuestions.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={(newValue) => {
              setItemsPerPage(newValue);
              setCurrentPage(1);
            }}
          />
        )}
        
        {/* Image Lightbox Modal */}
        <Dialog open={!!enlargedImage} onOpenChange={() => setEnlargedImage(null)}>
          <DialogContent className="max-w-4xl w-full p-0 bg-black/95 border-none">
            <button
              onClick={() => setEnlargedImage(null)}
              className="absolute top-4 right-4 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6 text-white" />
            </button>
            {enlargedImage && (
              <div className="flex flex-col items-center justify-center p-4">
                <img
                  src={enlargedImage.url}
                  alt={enlargedImage.caption || 'Enlarged image'}
                  className="max-w-full max-h-[80vh] object-contain"
                />
                {enlargedImage.caption && (
                  <p className="text-white text-center mt-4 text-sm">{enlargedImage.caption}</p>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        
        {/* Report Question Modal */}
        <ReportQuestionModal
          open={reportModal.open}
          onClose={() => setReportModal({ open: false, questionId: null, questionText: '' })}
          questionId={reportModal.questionId}
          questionText={reportModal.questionText}
          questionType="written"
        />
        
        {/* Upgrade Modal */}
        <UpgradeModal 
          isOpen={showUpgrade} 
          onClose={() => setShowUpgrade(false)} 
          feature="answers"
          subscribedGrade={subscriptionStatus?.subscribed_grade}
          targetGrade={userGrade}
        />

        {/* Explainer Video Modal — YouTube / Dadan iframe */}
        {videoModal.open && (
          <VideoModal
            videoUrl={videoModal.url}
            onClose={() => setVideoModal({ open: false, url: null })}
          />
        )}
      </div>
    </Layout>
  );
};

export default WrittenAllQuestionsPage;
